import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import moment from "moment";
import ConfirmationModal from "../../../../components/Common/ConfirmationModal";
import TableSkeletonLoader from "../../../../components/Common/TableSkeletonLoader";
import InspectionScheduleModal from "../../../../components/Admin/InvestorPortal/InspectionScheduleModal";
import ServiceRequestTimeline from "../../../../components/Admin/InvestorPortal/ServiceRequestTimeline";
import InvestorPortalDetailHeader from "../../../../components/Admin/InvestorPortal/InvestorPortalDetailHeader";
import {
  DetailSection,
  DetailField,
  DetailFieldList,
} from "../../../../components/Admin/InvestorPortal/InvestorPortalDetailFields";
import InvestorPortalActionButton, {
  InvestorPortalActionStack,
} from "../../../../components/Admin/InvestorPortal/InvestorPortalActionButton";
import {
  ServiceRequestStatusBadge,
  ParticipantTypeBadge,
} from "../../../../components/Admin/InvestorPortal/AdminStatusBadge";
import { formatMoney } from "../../../../utils/currencyFormat";
import { convertToTitleCase } from "../../../../utils/helpers";
import {
  getServiceRequestActions,
  getRecommendedServiceRequestAction,
} from "../../../../store/admin/investorOperations/serviceRequestLifecycle";
import {
  STAFF_REAL_ESTATE_ACTIONS,
  useGetStaffRealEstateRequestQuery,
  useAssignStaffRealEstateRequestMutation,
  useAddStaffRealEstateNoteMutation,
  useStaffRealEstateActionMutation,
  useScheduleStaffRealEstateInspectionMutation,
} from "../../../../store/admin/investorOperations/staffRealEstateApi";
import { useGetStaffListQuery } from "../../../../store/admin/staff";
import { SERVICE_REQUEST_TYPE } from "../../../../store/admin/investorOperations/constants";
import { buildScheduleInspectionPayload } from "../../../../utils/staffRealEstateAdapter";
import { withRealEstateGuard } from "../withInvestorPortalGuard";

function RecommendedActionCard({ status }) {
  const rec = getRecommendedServiceRequestAction(SERVICE_REQUEST_TYPE.REAL_ESTATE, status);
  return (
    <DetailSection title="Recommended next action">
      <p className="fw-semibold mb-1" style={{ color: "#383838", fontSize: 14 }}>
        {rec.label}
      </p>
      <p className="text-muted small mb-0">{rec.description}</p>
    </DetailSection>
  );
}

function ServiceRequestDetailPage() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [noteText, setNoteText] = useState("");
  const [reasonModal, setReasonModal] = useState(null);
  const [reasonText, setReasonText] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [assignStaffId, setAssignStaffId] = useState("");
  const [showInspectionModal, setShowInspectionModal] = useState(false);

  const { data: request, isFetching, isError } = useGetStaffRealEstateRequestQuery(requestId, {
    skip: !requestId,
  });
  const [runAction, { isLoading: isActionLoading }] = useStaffRealEstateActionMutation();
  const [addNote, { isLoading: isNoteLoading }] = useAddStaffRealEstateNoteMutation();
  const [assignStaff, { isLoading: isAssignLoading }] = useAssignStaffRealEstateRequestMutation();
  const [scheduleInspection, { isLoading: isScheduleLoading }] =
    useScheduleStaffRealEstateInspectionMutation();
  const { data: staffListData } = useGetStaffListQuery(1);
  const staffOptions = useMemo(() => staffListData?.staffs || [], [staffListData?.staffs]);

  const actions = useMemo(
    () => getServiceRequestActions(SERVICE_REQUEST_TYPE.REAL_ESTATE, request?.status),
    [request?.status]
  );

  useEffect(() => {
    if (request?.assignedStaffId != null) {
      setAssignStaffId(String(request.assignedStaffId));
    }
  }, [request?.assignedStaffId]);

  if (isFetching && !request) {
    return (
      <div className="pb-4">
        <TableSkeletonLoader rows={8} columns={2} />
      </div>
    );
  }

  if (!request || isError) {
    return <Navigate to="/super_admin/investor_portal/requests/real-estate" replace />;
  }

  const refKey = request.requestReference || requestId;

  const performAction = async (action, label, body) => {
    try {
      await runAction({ id: refKey, action, body }).unwrap();
      toast.success(label);
      setReasonModal(null);
      setReasonText("");
      setConfirm(null);
    } catch (error) {
      toast.error(error?.data?.message || "Action failed.");
    }
  };

  const handleActionClick = (actionDef) => {
    if (actionDef.action === "schedule") {
      setShowInspectionModal(true);
      return;
    }
    if (actionDef.action === "decline" || actionDef.action === "cancel") {
      setReasonModal({
        type: actionDef.action,
        label: actionDef.label,
        action:
          actionDef.action === "decline"
            ? STAFF_REAL_ESTATE_ACTIONS.decline
            : STAFF_REAL_ESTATE_ACTIONS.cancel,
      });
      setReasonText("");
      return;
    }
    if (
      actionDef.action === STAFF_REAL_ESTATE_ACTIONS.cancelInspection ||
      actionDef.action === STAFF_REAL_ESTATE_ACTIONS.completeInspection
    ) {
      setConfirm({
        title: actionDef.label,
        message: `${actionDef.label} for ${refKey}?`,
        action: actionDef.action,
        label: actionDef.label,
      });
      return;
    }

    setConfirm({
      title: actionDef.label,
      message: `${actionDef.label} for ${refKey}?`,
      action: actionDef.action,
      label: actionDef.label,
    });
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    try {
      await addNote({ id: refKey, note: noteText.trim() }).unwrap();
      setNoteText("");
      toast.success("Note added");
    } catch (error) {
      toast.error(error?.data?.message || "Unable to add note.");
    }
  };

  const handleAssign = async () => {
    if (!assignStaffId) {
      toast.error("Select a staff member.");
      return;
    }
    try {
      await assignStaff({
        id: refKey,
        assigned_staff_id: Number(assignStaffId),
      }).unwrap();
      toast.success("Staff assigned");
    } catch (error) {
      toast.error(error?.data?.message || "Unable to assign staff.");
    }
  };

  const handleScheduleSubmit = async (values) => {
    try {
      const body = buildScheduleInspectionPayload(values);
      await scheduleInspection({ id: refKey, body }).unwrap();
      toast.success("Inspection scheduled");
      setShowInspectionModal(false);
    } catch (error) {
      toast.error(error?.data?.message || "Unable to schedule inspection.");
    }
  };

  const re = request.realEstate || {};
  const inspection = request.inspection;
  const timelineEntries = (request.timeline || []).map((e) => ({
    id: e.id,
    status: e.status,
    message: e.message,
    staffName: e.staffName,
    at: e.at,
  }));

  return (
    <div className="pb-4">
      <InvestorPortalDetailHeader
        onBack={() => navigate("/super_admin/investor_portal/requests/real-estate")}
        title={request.title || re.propertyName || "Property request"}
        highlight={refKey}
      />
      <div className="d-flex flex-wrap gap-2 mb-3">
        <ServiceRequestStatusBadge status={request.status} />
        <ParticipantTypeBadge type={request.customerType || request.accountType} />
      </div>

      <div className="row g-3">
        <div className="col-12 col-lg-8">
          <DetailSection title="Customer">
            <DetailFieldList>
              <DetailField label="Name" value={request.customerName || "—"} />
              <DetailField label="Email" value={request.customerEmail || "—"} />
              <DetailField label="Phone" value={request.customerPhone || "—"} />
              <DetailField
                label="Account type"
                value={convertToTitleCase(String(request.accountType || "").replace(/_/g, " "))}
              />
              <DetailField
                label="Preferred contact"
                value={
                  request.preferredContact
                    ? convertToTitleCase(request.preferredContact)
                    : "—"
                }
              />
            </DetailFieldList>
          </DetailSection>

          <DetailSection title="Property request">
            <DetailFieldList>
              <DetailField label="Property" value={re.propertyName || request.title || "—"} />
              <DetailField label="Location" value={re.location || "—"} />
              <DetailField label="Units / plots" value={re.unitsRequested ?? "—"} />
              <DetailField
                label="Estimated value"
                value={
                  request.estimatedValueNgn != null ? formatMoney(request.estimatedValueNgn) : "—"
                }
              />
              <DetailField
                label="Inspection preference"
                value={
                  re.inspectionPreference
                    ? convertToTitleCase(re.inspectionPreference)
                    : "—"
                }
              />
              <DetailField
                label="Documentation interest"
                value={re.documentationInterest ? "Yes" : "No"}
              />
              <DetailField label="Customer note" value={request.customerNote || "—"} />
              <DetailField
                label="Submitted"
                value={
                  request.submittedAt
                    ? moment(request.submittedAt).format("DD MMM YYYY, HH:mm")
                    : "—"
                }
              />
            </DetailFieldList>
          </DetailSection>

          {inspection ? (
            <DetailSection title="Current inspection">
              <DetailFieldList>
                <DetailField label="Status" value={convertToTitleCase(inspection.status || "—")} />
                <DetailField
                  label="Date / time"
                  value={`${inspection.date || "—"}${inspection.time ? ` · ${inspection.time}` : ""}`}
                />
                <DetailField label="Location" value={inspection.location || "—"} />
                <DetailField label="Assigned staff" value={inspection.assignedStaffName || "—"} />
                <DetailField label="Note" value={inspection.note || "—"} />
              </DetailFieldList>
            </DetailSection>
          ) : null}

          <DetailSection title="Timeline">
            <ServiceRequestTimeline entries={timelineEntries} />
          </DetailSection>

          <DetailSection title="Internal notes">
            {!request.staffNotes?.length ? (
              <p className="text-muted small mb-3">No internal notes yet.</p>
            ) : (
              <ul className="list-unstyled mb-3">
                {request.staffNotes.map((n) => (
                  <li key={n.id} className="mb-3 pb-2 border-bottom">
                    <div className="d-flex justify-content-between gap-2">
                      <span className="fw-semibold small">{n.staffName || "Staff"}</span>
                      <span className="text-muted small">
                        {n.at ? moment(n.at).format("DD MMM YYYY, HH:mm") : ""}
                      </span>
                    </div>
                    <p className="small mb-0 mt-1" style={{ color: "#45464e" }}>
                      {n.text}
                    </p>
                  </li>
                ))}
              </ul>
            )}
            <textarea
              className="form-control mb-2"
              rows={3}
              placeholder="Add an internal note…"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
            />
            <InvestorPortalActionButton
              btnText={isNoteLoading ? "Saving…" : "Add note"}
              variant="outline"
              fullWidth={false}
              disabled={!noteText.trim() || isNoteLoading}
              onClick={handleAddNote}
            />
          </DetailSection>
        </div>

        <div className="col-12 col-lg-4">
          <RecommendedActionCard status={request.status} />

          <DetailSection title="Assignment">
            <p className="small text-muted mb-2">
              Currently: <strong>{request.assignedStaffName || "Unassigned"}</strong>
            </p>
            <select
              className="form-select mb-2"
              value={assignStaffId}
              onChange={(e) => setAssignStaffId(e.target.value)}
            >
              <option value="">Select staff…</option>
              {staffOptions.map((s) => (
                <option key={s.id} value={s.id}>
                  {`${s.first_name || ""} ${s.last_name || ""}`.trim() || s.email}
                </option>
              ))}
            </select>
            <InvestorPortalActionButton
              btnText={isAssignLoading ? "Assigning…" : "Assign staff"}
              variant="outline"
              fullWidth
              disabled={!assignStaffId || isAssignLoading}
              onClick={handleAssign}
            />
            <p className="text-muted small mt-2 mb-0">
              Staff list uses the first page of company staff. Search across all staff is a future
              enhancement.
            </p>
          </DetailSection>

          {!request.isTerminal ? (
            <DetailSection title="Actions">
              <InvestorPortalActionStack>
                {actions.map((actionDef) => (
                  <InvestorPortalActionButton
                    key={`${actionDef.label}-${actionDef.action || actionDef.target}`}
                    btnText={actionDef.label}
                    variant={actionDef.variant || "outline"}
                    fullWidth
                    disabled={isActionLoading || isScheduleLoading}
                    onClick={() => handleActionClick(actionDef)}
                  />
                ))}
              </InvestorPortalActionStack>
            </DetailSection>
          ) : null}
        </div>
      </div>

      <InspectionScheduleModal
        show={showInspectionModal}
        request={request}
        staffOptions={staffOptions}
        onClose={() => setShowInspectionModal(false)}
        onSubmit={handleScheduleSubmit}
      />

      <ConfirmationModal
        isOpen={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        title={confirm?.title || "Confirm"}
        message={confirm?.message || ""}
        confirmText="Continue"
        isLoading={isActionLoading}
        onConfirm={() =>
          confirm && performAction(confirm.action, confirm.label || "Updated")
        }
      />

      <ConfirmationModal
        isOpen={Boolean(reasonModal)}
        onClose={() => setReasonModal(null)}
        title={reasonModal?.label || "Confirm"}
        message={`Add an optional reason, then confirm.\n\nCurrent reason: ${reasonText || "(none)"}`}
        confirmText={reasonModal?.label || "Confirm"}
        isLoading={isActionLoading}
        onConfirm={() => {
          const next = window.prompt("Reason (optional)", reasonText || "");
          if (next === null) return;
          setReasonText(next);
          reasonModal &&
            performAction(reasonModal.action, reasonModal.label, {
              reason: next.trim() || undefined,
            });
        }}
      />
    </div>
  );
}

export default withRealEstateGuard(ServiceRequestDetailPage);
